import React, { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react"
import { Button, Form, Input, List, Modal, Select, Spin, Upload } from "antd"
import { UploadOutlined } from "@ant-design/icons"

import CustomDrawer from "components-old/ui/CustomDrawer"
import request from "lib/request"
import { endpoints } from "config/endpoints.config"
import toast from "lib/toast"
import { ITemplateMessage } from "components-old/inbox/NewMessageModal/NewMessageTemplate"
import styles from "components-old/inbox/NewMessageModal/style.module.css"
import { useStore } from "hooks"
import { general } from "store"
import { Delete03Icon, PencilEdit02Icon } from "components-old/icons"
import FormModal from "components-old/forms/FormModal"
import { cn, showDeleteModalConfirm } from "lib/utils"
import { useQuery } from "react-query"
import { IPaginatedResponse } from "models/generic"
import SearchInput from "components-old/common/SettingsPage/SearchInput"
import { debounce } from "lodash"
import Tooltip from "components-old/Tooltip"
import { TagItem } from "components-old/chat/TagInput"
import { tagColors } from "pages/settings/tags/EditTag"
import StatusTag from "components-old/chat/StatusTag"

const LANGUAGE_OPTIONS = [
  { label: "Português (Brasil)", value: "pt_BR" },
  { label: "English (US)", value: "en_US" },
  { label: "Español", value: "es_ES" },
]

interface WhatsappTemplatesDrawerProps {
  channelId?: string
  visible: boolean
  onClose: () => void
}

interface TemplateFormValues {
  name: string
  language: string
  category: string
  headerType?: "NONE" | "TEXT" | "IMAGE" | "VIDEO" | "DOCUMENT"
  header?: string
  headerMedia?: string

  body: string
  footer?: string
}

function WhatsappTemplatesDrawer(props: WhatsappTemplatesDrawerProps) {
  const { uploadFile, getAsset } = useStore(general)

  // const [templates, setTemplates] = useState<ITemplateMessage[]>([])
  // const [loading, setLoading] = useState(false)
  const [formVisible, setFormVisible] = useState(false)
  const [editing, setEditing] = useState<ITemplateMessage | null>(null)
  const [_search, setSearch] = useState("")
  const [form] = Form.useForm<TemplateFormValues>()
  const [preview, setPreview] = useState<{
    headerType?: TemplateFormValues["headerType"]
    header?: string
    headerMedia?: string
    body: string
    footer?: string
  }>({
    headerType: "NONE",
    header: "",
    headerMedia: "",

    body: "",
    footer: "",
  })

  const search = useDeferredValue(_search)

  const templatesState = useQuery<IPaginatedResponse<ITemplateMessage>>({
    queryKey: ["whatsapp-templates", props.channelId],
    queryFn: () => request.get(`${endpoints.WHATSAPP_WBA_CLOUD}/${props.channelId}/templates-raw`).then((res) => res.data),
    enabled: !!props.visible,
  })

  // const fetchTemplates = useCallback(() => {
  //   if (!props.channelId) return
  //   setLoading(true)
  //   request
  //     .get(`${endpoints.WHATSAPP_WBA_CLOUD}/${props.channelId}/templates-raw`)
  //     .then((res) => {
  //       setTemplates(res.data.data)
  //     })
  //     .finally(() => setLoading(false))
  // }, [props.channelId])

  // useEffect(() => {
  //   if (props.visible) {
  //     fetchTemplates()
  //   }
  // }, [props.visible, fetchTemplates])

  const openForm = (template?: ITemplateMessage) => {
    setEditing(template || null)
    if (template) {
      const headerComponent = template.components.find((c) => c.type === "HEADER")
      const body = template.components.find((c) => c.type === "BODY")?.text
      const footer = template.components.find((c) => c.type === "FOOTER")?.text
      const headerType = headerComponent?.format || "NONE"
      const headerText = headerType === "TEXT" ? headerComponent?.text : undefined
      const headerMedia = headerType !== "TEXT" ? (headerComponent as any)?.example?.header_handle : undefined

      form.setFieldsValue({
        name: template.name,
        language: template.language,
        category: template.category,
        headerType,
        header: headerText,
        headerMedia,
        body: body || "",
        footer,
      })
      setPreview({
        headerType,
        header: headerText || "",
        headerMedia: headerMedia || "",
        body: body || "",
        footer: footer || "",
      })
    } else {
      form.resetFields()
      form.setFieldsValue({ headerType: "NONE" })
      setPreview({ headerType: "NONE", header: "", headerMedia: "", body: "", footer: "" })
    }
    setFormVisible(true)
  }

  const submitForm = async () => {
    const values = await form.validateFields()
    const components: any[] = []
    if (values.headerType && values.headerType !== "NONE") {
      if (values.headerType === "TEXT") {
        components.push({ type: "HEADER", format: "TEXT", text: values.header })
      } else {
        components.push({
          type: "HEADER",
          format: values.headerType,
          example: { header_handle: [values.headerMedia] },
        })
      }
    }
    components.push({ type: "BODY", text: values.body })
    if (values.footer) {
      components.push({ type: "FOOTER", text: values.footer })
    }

    const payload = {
      name: values.name,
      language: values.language,
      category: values.category,
      components,
    }

    if (editing?.id) {
      await request.put(`${endpoints.WHATSAPP_WBA_CLOUD}/${props.channelId}/templates/${editing.id}`, payload)
      toast.success("Template atualizado.")
    } else {
      await request.post(`${endpoints.WHATSAPP_WBA_CLOUD}/${props.channelId}/templates`, payload)
      toast.success("Template criado.")
    }
    setFormVisible(false)
    // fetchTemplates()
    templatesState.refetch()
  }

  const deleteTemplate = (template: ITemplateMessage) => {
    showDeleteModalConfirm({
      description: "Tem certeza que deseja excluir esse template? Não poderá desfazer.",
      onConfirm: () => {
        request.delete(`${endpoints.WHATSAPP_WBA_CLOUD}/${props.channelId}/templates/${template.id}`).then(() => {
          toast.success("Template removido.")
          templatesState.refetch()
        })
      },
      zIndex: 9999999,
    })
    // Modal.confirm({
    //   title: "Remover template?",
    //   onOk: async () => {
    //     await request.delete(`${endpoints.WHATSAPP_WBA_CLOUD}/${props.channelId}/templates/${template.id}`)
    //     toast.success("Template removido.")
    //     // fetchTemplates()
    //     templatesState.refetch()
    //   },
    // })
  }

  const templates = useMemo(() => {
    return templatesState.data?.data?.filter((t) => t.name.toLowerCase().includes(search.toLowerCase())) || []
  }, [templatesState.data, search])

  return (
    <>
      <CustomDrawer
        title="Templates do WhatsApp"
        placement="right"
        width={700}
        visible={props.visible}
        onClose={props.onClose}
        destroyOnClose
      >
        <div className="flex items-center gap-4 mb-5 mt-0.5">
          <SearchInput
            autoFocus
            onChange={(e) => {
              setSearch(e.target.value)
            }}
            allowClear
            className="!w-full"
          />
          <Button type="primary" onClick={() => openForm()}>
            Novo template
          </Button>
        </div>
        <Spin spinning={templatesState.isLoading} className="mt-5">
          <div className="flex flex-col">
            {templates.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between py-4 last:!border-transparent"
                style={{
                  borderBottom: "1px solid var(--divider)",
                }}
              >
                <div className="flex-1 font-medium">{item.name}</div>
                <div className="flex-1">
                  {/* <TagItem colors={item.status === "APPROVED" ? tagColors.success : tagColors.light}>{item.language}</TagItem> */}
                  <StatusTag status={item.status === "APPROVED" ? "success" : undefined} icon={false} noBorder>
                    {item.status}
                  </StatusTag>
                </div>
                <div className="flex gap-2">
                  <Tooltip title="Editar">
                    <Button type="text" onClick={() => openForm(item)} icon={<PencilEdit02Icon />} />
                  </Tooltip>
                  <Tooltip title="Remover">
                    <Button type="text" onClick={() => deleteTemplate(item)} icon={<Delete03Icon />} />
                  </Tooltip>
                </div>
              </div>
            ))}
          </div>
        </Spin>
      </CustomDrawer>
      {formVisible && (
        <FormModal
          title={editing ? "Editar template" : "Novo template"}
          onBack={() => setFormVisible(false)}
          // onOk={submitForm}
          visible={formVisible}
          width={800}
          type="modal"
          requiredMark
          hideSuccessMessage
          onSubmit={submitForm}
          form={form}
          // key={}
          formProps={{
            initialValues: { headerType: "NONE" },
            onValuesChange: (_, values) => {
              setPreview({
                headerType: values.headerType,
                header: values.header,
                headerMedia: values.headerMedia,

                body: values.body,
                footer: values.footer,
              })
            },
          }}
        >
          <div className="flex gap-4">
            <div className="flex-1">
              <Form.Item label="Nome" name="name" rules={[{ required: true, message: "Campo obrigatório" }]}>
                <Input onChange={(e) => {
                  const value = e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9_]/g, '_')
                    .replace(/\s+/g, '_');
                  e.target.value = value;
                  form.setFieldValue('name', value);
                }} />
              </Form.Item>
              <Form.Item label="Linguagem" name="language" rules={[{ required: true, message: "Campo obrigatório" }]}>
                <Select options={LANGUAGE_OPTIONS} showSearch />
              </Form.Item>
              <Form.Item label="Categoria" name="category" rules={[{ required: true, message: "Campo obrigatório" }]}>
                <Select>
                  <Select.Option value="MARKETING">Marketing</Select.Option>
                  <Select.Option value="UTILITY">Utility</Select.Option>
                  <Select.Option value="AUTHENTICATION">Authentication</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item label="Tipo de cabeçalho" name="headerType">
                <Select>
                  <Select.Option value="NONE">Sem cabeçalho</Select.Option>
                  <Select.Option value="TEXT">Texto</Select.Option>
                  <Select.Option value="IMAGE">Imagem</Select.Option>
                  <Select.Option value="VIDEO">Vídeo</Select.Option>
                  <Select.Option value="DOCUMENT">Documento</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item shouldUpdate={(prev, cur) => prev.headerType !== cur.headerType} noStyle>
                {({ getFieldValue, setFieldsValue }) => {
                  const type = getFieldValue("headerType")
                  if (type === "TEXT") {
                    return (
                      <Form.Item label="Cabeçalho" name="header">
                        <Input />
                      </Form.Item>
                    )
                  }
                  if (["IMAGE", "VIDEO", "DOCUMENT"].includes(type)) {
                    return (
                      <Form.Item label="Mídia" name="headerMedia" rules={[{ required: true, message: "Campo obrigatório" }]}>
                        <>
                          <Upload
                            showUploadList={false}
                            beforeUpload={(file) => {
                              const formData = new FormData()
                              formData.append("file", file)
                              uploadFile({ data: formData }).then((res) => {
                                const url = getAsset(res[0].fileName)
                                setFieldsValue({ headerMedia: url })
                                setPreview((p) => ({ ...p, headerMedia: url }))
                              })
                              return false
                            }}
                          >
                            <Button icon={<UploadOutlined />}>Upload</Button>
                          </Upload>
                          {getFieldValue("headerMedia") && type === "IMAGE" && (
                            <img src={getFieldValue("headerMedia")} className="mt-2 max-h-40" />
                          )}
                          {getFieldValue("headerMedia") && type === "DOCUMENT" && (
                            <a href={getFieldValue("headerMedia")} target="_blank" rel="noreferrer" className="block mt-2">
                              Documento enviado
                            </a>
                          )}
                          {getFieldValue("headerMedia") && type === "VIDEO" && (
                            <video src={getFieldValue("headerMedia")} controls className="mt-2 max-h-40" />
                          )}
                        </>
                      </Form.Item>
                    )
                  }
                  return null
                }}
              </Form.Item>
              <Form.Item label="Corpo" name="body" rules={[{ required: true, message: "Campo obrigatório" }]}>
                <Input.TextArea rows={4} />
              </Form.Item>
              <Form.Item label="Rodapé" name="footer">
                <Input />
              </Form.Item>
            </div>
            <div className={"flex-1 flex items-start justify-center"}>
              <div className={cn(styles.whatsapp_preview, "h-full items-center")}>
                <div className={cn(styles.whatsapp_preview_balloon, "min-w-[246px] min-h-[40px]")}>
                  {preview.headerType === "TEXT" && preview.header && <h3>{preview.header}</h3>}
                  {preview.headerType === "IMAGE" && preview.headerMedia && <img src={preview.headerMedia} className="mb-2 max-w-full !w-full !h-auto !max-h-[130px] object-cover" />}
                  {preview.headerType === "VIDEO" && preview.headerMedia && (
                    <video src={preview.headerMedia} controls className="mb-2 max-w-full" />
                  )}
                  {preview.headerType === "DOCUMENT" && preview.headerMedia && (
                    <a href={preview.headerMedia} target="_blank" rel="noreferrer" className="mb-2 block">
                      Documento
                    </a>
                  )}

                  <p>{preview.body}</p>
                  {preview.footer && <p className={styles.bottom}>{preview.footer}</p>}
                </div>
              </div>
            </div>
          </div>
        </FormModal>
      )}
    </>
  )
}

export default WhatsappTemplatesDrawer
